// ============================================================
// useActorProfiles - the attacker "profile" menu, as roster operations.
//
// Before the roster, a profile was a named 40-slot grid living in one
// shared document. Now the dropdown lists COMBATANTS, because under the
// unified model you can act as anyone in the fight. So:
//
//   load   → act as that combatant
//   new    → add a blank combatant and act as them
//   saveAs → duplicate the actor under a new name
//   delete → remove that combatant from the roster
//
// Replaces the attacker half of useProfileManagement.
// ============================================================
import { useCallback } from 'react';
import { combatantFromCharacter } from '../engine/roster/factories';
import { combatantIdForProfile } from '../engine/roster/legacyAdapter';
import { emptyCharacter } from '../engine/character/factories';

/**
 * @param {Object}   params
 * @param {Array}    params.roster
 * @param {Function} params.setRoster
 * @param {string|null} params.actorId
 * @param {Function} params.setActorId
 * @param {Object|null} params.actor
 * @param {Function} params.setSelectedSlot
 * @param {Function} params.setShowAtkMenu
 * @param {Function} params.setNewAtkName
 * @param {Function} params.isLocked
 * @param {Function} params.showDialogAsync
 */
export default function useActorProfiles(params) {
  const handleLoadAtkProfile = useLoadProfile(params);
  const handleNewAtkProfile = useNewProfile(params);
  const handleSaveAtkProfile = useSaveProfileAs(params);
  const handleDeleteAtkProfile = useDeleteProfile(params);
  return { handleLoadAtkProfile, handleNewAtkProfile, handleSaveAtkProfile, handleDeleteAtkProfile };
}

/** Load: act as that combatant. */
function useLoadProfile({ roster, setActorId, setSelectedSlot, setShowAtkMenu }) {
  return useCallback(
    (name) => {
      setActorId(combatantIdForProfile(roster, name));
      setSelectedSlot(null);
      setShowAtkMenu(false);
    },
    [roster, setActorId, setSelectedSlot, setShowAtkMenu],
  );
}

/** New: add a blank combatant, named at a prompt, and act as them. */
function useNewProfile({ isLocked, showDialogAsync, roster, setRoster, setActorId, setSelectedSlot, setShowAtkMenu }) {
  return useCallback(async () => {
    if (isLocked('editProfiles')) return;
    setShowAtkMenu(false);
    const name = await showDialogAsync('prompt', 'New attacker name:', '');
    if (!name) return;
    const sheet = emptyCharacter();
    sheet.name = name;
    const combatant = combatantFromCharacter(sheet, { kind: 'statblock', name, order: roster.length });
    setRoster((prev) => [...prev, combatant]);
    setActorId(combatant.id);
    setSelectedSlot(null);
  }, [isLocked, showDialogAsync, roster.length, setRoster, setActorId, setSelectedSlot, setShowAtkMenu]);
}

/**
 * Save as: duplicate the actor under a new name, so the dropdown gains an
 * entry carrying the same weapons - the closest honest equivalent of
 * snapshotting the slots. A shared loadout library (assign one kit to many
 * combatants) lands in D4.
 */
function useSaveProfileAs({ actor, roster, isLocked, setRoster, setActorId, setShowAtkMenu, setNewAtkName }) {
  return useCallback(
    (name) => {
      if (!name || !actor || isLocked('editProfiles')) return;
      const copy = combatantFromCharacter(actor.sheet, { name, kind: actor.kind, order: roster.length });
      copy.slotOrder = actor.slotOrder;
      setRoster((prev) => [...prev, copy]);
      setActorId(copy.id);
      setShowAtkMenu(false);
      setNewAtkName('');
    },
    [actor, roster.length, isLocked, setRoster, setActorId, setShowAtkMenu, setNewAtkName],
  );
}

/** Delete: remove that combatant from the roster, after a confirm - and stop acting as them. */
function useDeleteProfile(p) {
  const { roster, actorId, isLocked, showDialogAsync, setRoster, setActorId, setSelectedSlot, setShowAtkMenu } = p;
  return useCallback(
    async (name) => {
      if (!name || isLocked('editProfiles')) return;
      const id = combatantIdForProfile(roster, name);
      if (!id) return; // the synthetic "Default" entry names nobody
      setShowAtkMenu(false);
      const confirmed = await showDialogAsync('confirm', `Remove "${name}" from the roster?`);
      if (!confirmed) return;
      setRoster((prev) => prev.filter((c) => c.id !== id));
      if (actorId === id) {
        setActorId(null);
        setSelectedSlot(null);
      }
    },
    [roster, actorId, isLocked, showDialogAsync, setRoster, setActorId, setSelectedSlot, setShowAtkMenu],
  );
}
